/**
 * **Placement fit** — the one home of "does this footprint fit at this anchor in this
 * encounter": {@link movementContext} + `@lancer-vtt/space`'s `footprintEndpointFits` +
 * `inBounds`, bound to an `EncounterState`. Every endpoint legality check (deploy, `place_token`,
 * teleport, a spawned pilot/drone/mine, a forced-move or drag landing) goes through here rather
 * than re-binding the space primitive privately; `placementFit.pin.test.ts` fails on a new
 * direct binding anywhere else.
 *
 * An endpoint sees full occupancy — `blocked ∪ noStop` — so a unit may pass through an ally or a
 * smaller hostile but never stop on one. Occupancy is classified relative to `mover`
 * (`movementContext`'s third argument): the placed combatant's own id, so it never blocks
 * itself; a sentinel id no live combatant resolves to, for a brand-new spawn (every placed
 * combatant is then solid); or `{ exceptId, as: "flying" }` for a per-walk flight override.
 *
 * Two arms. The **boolean arm** ({@link placementFits} / {@link placementFitter} /
 * {@link endpointFitterIn}) is the cheap one a search loop calls per candidate. The **cause arm**
 * ({@link placementFailure}) says *why* a fit fails, for a reject message. The space layer is
 * cause-blind (terrain-solid and occupant cells are already one set by the time the predicate
 * runs), so the cause is re-derived from `state.terrain` and the placed combatants — but only for
 * a cell the same context forbids, so `placementFailure(...) === null` exactly when
 * `placementFits(...)` is true.
 */

import { type MovementContext, type World, footprintEndpointFits } from "@lancer-vtt/space";
import { TERRAFORMING_STATUS, movementContext, placements } from "./movement";
import { solidTerrainKeys } from "./terrain";
import { hasStatus, type Cell, type EncounterState } from "./types";
import { worldFor } from "./world";

/** Whose occupancy the fit is judged relative to — exactly {@link movementContext}'s third argument. */
export type PlacementMover = Parameters<typeof movementContext>[2];

/** Named arguments for {@link placementFitter}. */
export type PlacementFitArgs = {
  mover: PlacementMover;
  /** Footprint size (0.5 for a pilot or drone). */
  size: number;
};

/**
 * The endpoint predicate for a caller that already holds a {@link movementContext} (a walk that
 * also needs the context's `blocked` set for pass-through steps). Prefer {@link placementFitter}
 * when you only need the fit.
 */
export function endpointFitterIn(world: World<Cell>, ctx: MovementContext<Cell>, size: number): (anchor: Cell) => boolean {
  return (anchor: Cell) =>
    footprintEndpointFits({ space: world, anchor, size, blocked: ctx.blocked, noStop: ctx.noStop, inBounds: (p: Cell) => world.inBounds(p) });
}

/**
 * The fit predicate for one mover and size, with the movement context built once — use this
 * to test many candidate anchors against the same state.
 */
export function placementFitter(state: EncounterState, args: PlacementFitArgs): (anchor: Cell) => boolean {
  const world = worldFor(state);
  return endpointFitterIn(world, movementContext(world, state, args.mover), args.size);
}

/** Does a `size` footprint fit at `anchor`, judged relative to `mover`? A one-shot {@link placementFitter}. */
export function placementFits(state: EncounterState, args: PlacementFitArgs & { anchor: Cell }): boolean {
  return placementFitter(state, args)(args.anchor);
}

/**
 * Can `a` and `b` swap places: `a`'s footprint at `b`'s anchor and `b`'s at `a`'s, each a legal
 * endpoint for its own mover (Beckon: "Characters can only be swapped to spaces they could normally
 * stand or move on"). Both are lifted first so neither blocks the other, and `b` is then judged
 * with `a` already in its new place, so two footprints of different sizes never overlap. Either
 * one unknown or unplaced → `false`.
 */
export function swapFits(state: EncounterState, args: { a: string; b: string }): boolean {
  const a = state.combatants[args.a];
  const b = state.combatants[args.b];
  if (a?.pos == null || b?.pos == null || a.id === b.id) return false;
  const lifted: EncounterState = { ...state, combatants: { ...state.combatants, [a.id]: { ...a, pos: null }, [b.id]: { ...b, pos: null } } };
  if (!placementFits(lifted, { mover: a.id, size: a.base.size, anchor: b.pos })) return false;
  const aMoved: EncounterState = { ...lifted, combatants: { ...lifted.combatants, [a.id]: { ...a, pos: b.pos } } };
  return placementFits(aMoved, { mover: b.id, size: b.base.size, anchor: a.pos });
}

/**
 * Why a footprint does not fit, naming the first offending cell of the highest-priority cause.
 * Priority is off-map, then solid terrain, then occupied: the two public causes (both drawn on
 * every client's board) win over the one that can reveal a concealed unit.
 *
 * No grid-mismatch arm: every endpoint handler rejects a wrong-grid cell through
 * `cellGridMismatch` before it reaches the fit, so the fit never sees one.
 */
export type PlacementFailure =
  | { cause: "off-map"; cell: Cell }
  | { cause: "solid-terrain"; cell: Cell }
  /** `occupantId` is the placed combatant covering `cell`, or null if none resolves (defensive). */
  | { cause: "occupied"; cell: Cell; occupantId: string | null };

/**
 * The cause arm: `null` when the footprint fits (exactly when {@link placementFits} is true),
 * otherwise the {@link PlacementFailure}. Not for search loops — it rebuilds the context and
 * re-scans the placements; use {@link placementFitter} there.
 */
export function placementFailure(state: EncounterState, args: PlacementFitArgs & { anchor: Cell }): PlacementFailure | null {
  const world = worldFor(state);
  const ctx = movementContext(world, state, args.mover);
  const cells = world.footprint(args.anchor, args.size).cells();
  const offMap = cells.find((c) => !world.inBounds(c));
  if (offMap) return { cause: "off-map", cell: offMap };
  const forbidden = cells.filter((c) => {
    const key = world.key(c);
    return ctx.blocked.has(key) || (ctx.noStop?.has(key) ?? false);
  });
  if (forbidden.length === 0) return null;
  // Attribution mirrors `movementContext`'s terrain union: solid terrain is forbidden to every
  // mover except a Terraforming one, so for that mover a forbidden cell is an occupant's.
  const moverId = typeof args.mover === "string" ? args.mover : args.mover.exceptId;
  const mover = state.combatants[moverId];
  const terrainCounts = !(mover !== undefined && hasStatus(mover.statuses, TERRAFORMING_STATUS));
  if (terrainCounts) {
    const solid = solidTerrainKeys(state.terrain);
    const rock = forbidden.find((c) => solid.has(world.key(c)));
    if (rock) return { cause: "solid-terrain", cell: rock };
  }
  const cell = forbidden[0]!;
  const key = world.key(cell);
  const occupant = placements(state).find(
    (p) => p.id !== moverId && world.footprint(p.anchor, p.size).cells().some((c) => world.key(c) === key),
  );
  return { cause: "occupied", cell, occupantId: occupant?.id ?? null };
}
